import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdClose, MdSearch, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import BeltBadge from '../common/BeltBadge';
import Button from '../common/Button';
import { getStudents } from '../../services/studentService';
import { getClassEnrollments, bulkEnroll } from '../../services/enrollmentService';
import useClubVocab from '../../hooks/useClubVocab';
import { useConfirm } from '../common/ConfirmModal';

function EnrollStudentsModal({ classInfo, onClose, onSuccess }) {
  const v = useClubVocab();
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(new Set());
  const [hideEnrolled, setHideEnrolled] = useState(true);
  const queryClient = useQueryClient();
  const [confirm, confirmModal] = useConfirm();

  const { data: studentData } = useQuery({
    queryKey: ['all-students-enroll'],
    // limit must be an allowed page size (10/25/50/100); 100 = max active students
    // the picker loads in one go. Anything outside the allow-list is clamped to 10.
    queryFn:  () => getStudents({ limit: 100, isActive: 'true' }).then(r => r.data),
  });

  const { data: enrolledData } = useQuery({
    queryKey: ['class-enrollments', classInfo._id],
    queryFn:  () => getClassEnrollments(classInfo._id).then(r => r.data),
  });

  const enrolledIds = new Set(
    (enrolledData?.enrollments || []).map(e => e.student?._id?.toString())
  );

  const allStudents = (studentData?.students || []).map(p => ({
    profileId: p._id,
    userId:    p.user?._id,
    name:      p.user?.name || p.fullName,
    email:     p.user?.email,
    belt:      p.currentGrade,
    discipline:p.discipline,
  }));

  const filtered = useMemo(() => {
    let list = allStudents;
    if (hideEnrolled) {
      list = list.filter(s => !enrolledIds.has(s.userId?.toString()));
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => s.name?.toLowerCase().includes(q) || s.email?.toLowerCase().includes(q));
    }
    // Sort: matching discipline first
    return list.sort((a, b) => {
      const aMatch = a.discipline === classInfo.discipline ? -1 : 0;
      const bMatch = b.discipline === classInfo.discipline ? -1 : 0;
      return aMatch - bMatch;
    });
  }, [allStudents, search, hideEnrolled, enrolledIds]);

  const toggle = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(s => s.userId?.toString()).filter(Boolean)));
    }
  };

  const mutation = useMutation({
    mutationFn: (confirmCapacityOverride) =>
      bulkEnroll({ studentIds: Array.from(selected), classId: classInfo._id, confirmCapacityOverride }),
    onSuccess: ({ data }) => {
      toast.success(`Enrolled ${data.enrolled} ${data.enrolled !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()}`);
      if (data.failed?.length) {
        toast(`${data.failed.length} already enrolled`, { icon: 'ℹ️' });
      }
      queryClient.invalidateQueries({ queryKey: ['class-enrollments', classInfo._id] });
      queryClient.invalidateQueries({ queryKey: ['enrollments'] });
      onSuccess?.();
      onClose();
    },
  });

  const handleEnroll = async () => {
    try {
      await mutation.mutateAsync(false);
    } catch (e) {
      // Class is full — offer to add the whole selection anyway (one prompt, not one per student).
      if (e.response?.status === 409 && e.response?.data?.code === 'CLASS_FULL') {
        const ok = await confirm({
          title: 'Class is full',
          message: e.response.data.message,
          confirmLabel: 'Add anyway',
          destructive: false,
        });
        if (!ok) return;
        try {
          await mutation.mutateAsync(true);
        } catch {
          toast.error('Enrollment failed');
        }
      } else {
        toast.error('Enrollment failed');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="font-bold text-secondary text-base">Enroll {v.members}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{classInfo.name}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100">
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0 space-y-2">
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search ${v.members.toLowerCase()}…`}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={hideEnrolled} onChange={e => setHideEnrolled(e.target.checked)} className="accent-primary" />
              Hide already enrolled
            </label>
            <span className="text-xs text-gray-400">{filtered.length} available</span>
            {filtered.length > 0 && (
              <button onClick={toggleAll} className="text-xs text-primary hover:underline ml-auto">
                {selected.size === filtered.length ? 'Deselect all' : 'Select all'}
              </button>
            )}
          </div>
        </div>

        {/* Student list */}
        <div className="overflow-y-auto flex-1">
          {!filtered.length ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              {hideEnrolled && enrolledIds.size > 0
                ? `All eligible ${v.members.toLowerCase()} are already enrolled`
                : `No ${v.members.toLowerCase()} found`}
            </div>
          ) : (
            filtered.map(s => {
              const uid       = s.userId?.toString();
              const isChecked = selected.has(uid);
              const isAlready = enrolledIds.has(uid);
              return (
                <div
                  key={uid}
                  onClick={() => !isAlready && toggle(uid)}
                  className={`flex items-center gap-3 px-5 py-3 border-b border-gray-50 transition-colors
                    ${isAlready ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
                    ${isChecked ? 'bg-primary-light' : ''}`}
                >
                  <div className="text-primary text-xl flex-shrink-0">
                    {isAlready ? (
                      <span className="text-xs font-bold text-green-500 bg-green-50 px-2 py-1 rounded-full">enrolled</span>
                    ) : isChecked ? (
                      <MdCheckBox />
                    ) : (
                      <MdCheckBoxOutlineBlank className="text-gray-300" />
                    )}
                  </div>
                  <div className="w-8 h-8 bg-primary-light text-primary rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {(s.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-secondary truncate">{s.name}</p>
                    <p className="text-xs text-gray-400 truncate">{s.discipline}</p>
                  </div>
                  <BeltBadge belt={s.belt} size="xs" />
                </div>
              );
            })
          )}
        </div>

        {/* Sticky footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-white">
          <span className="text-sm text-gray-500">
            {selected.size > 0
              ? `${selected.size} ${selected.size !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()} selected`
              : `Select ${v.members.toLowerCase()} to enroll`}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              onClick={handleEnroll}
              disabled={selected.size === 0}
              loading={mutation.isPending}
            >
              Enroll {selected.size > 0 ? `(${selected.size})` : ''}
            </Button>
          </div>
        </div>
      </div>
      {confirmModal}
    </div>
  );
}

export default EnrollStudentsModal;
